import { useState, useEffect, useMemo } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useAdminReports, useReviewReport } from "@/hooks/use-reports";
import type { Report } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Flag,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";

export const Route = createFileRoute("/_main/exams/$slug/admin/reports")({
  component: AdminReportsPage,
});

function AdminReportsPage() {
  const { slug: examSlug } = Route.useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [comment, setComment] = useState("");

  const { data: allReports = [], isLoading: loading } = useAdminReports(
    statusFilter === "all" ? undefined : statusFilter,
  );
  const reports = useMemo(
    () => allReports.filter((r) => r.exam_slug === examSlug),
    [allReports, examSlug],
  );
  const reviewMutation = useReviewReport();
  const reviewing = reviewMutation.isPending;

  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.role !== "admin") {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  async function handleReview(action: "resolve" | "dismiss") {
    if (!selectedReport) return;
    try {
      await reviewMutation.mutateAsync({
        id: selectedReport.id,
        data: { action, comment: comment || undefined },
      });
      setReviewDialogOpen(false);
      setComment("");
      setSelectedReport(null);
    } catch {
      alert(t("common.error"));
    }
  }

  function statusBadge(status: string) {
    switch (status) {
      case "pending":
        return <Badge variant="secondary">{t("report.status_pending")}</Badge>;
      case "resolved":
        return <Badge variant="default">{t("report.status_resolved")}</Badge>;
      case "dismissed":
        return <Badge variant="outline">{t("report.status_dismissed")}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  }

  function reasonLabel(reason: string) {
    return t(`report.reason_${reason}`) || reason;
  }

  if (authLoading || !isAuthenticated || user?.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/exams/$slug/admin/questions"
              params={{ slug: examSlug }}
            >
              <Button variant="ghost" size="sm" className="cursor-pointer">
                <ArrowLeft className="mr-1 h-4 w-4" />
                {t("common.back")}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Flag className="h-5 w-5 text-red-500" />
                {t("report.admin_title")}
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                {t("report.admin_desc")}
              </p>
            </div>
          </div>
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value || "")}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">{t("report.status_pending")}</SelectItem>
              <SelectItem value="all">{t("common.all")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reports list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("report.list_title")}</CardTitle>
            <CardDescription>
              {t("common.total")} {reports.length}{t("common.count_suffix")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : reports.length === 0 ? (
              <p className="py-8 text-center text-sm text-zinc-400">
                {t("report.no_reports")}
              </p>
            ) : (
              <div className="space-y-3">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className="rounded-lg border border-zinc-100 p-4 dark:border-zinc-800"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">
                            #{report.question_number}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {reasonLabel(report.reason)}
                          </Badge>
                          {statusBadge(report.status)}
                        </div>
                        {report.description && (
                          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                            {report.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                          <span>{report.user_email || report.user_id.slice(0, 8)}</span>
                          <span>
                            {new Date(report.created_at).toLocaleDateString(
                              locale === "en" ? "en-US" : "ko-KR"
                            )}
                          </span>
                        </div>
                        {report.admin_comment && (
                          <p className="mt-2 text-xs text-zinc-500 border-l-2 border-zinc-300 pl-2">
                            {report.admin_comment}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to="/exams/$slug/questions"
                          params={{ slug: examSlug }}
                          search={{ search: String(report.question_number) }}
                        >
                          <Button variant="ghost" size="sm" className="cursor-pointer text-xs">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            {t("report.view_question")}
                          </Button>
                        </Link>
                        {report.status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="cursor-pointer text-xs"
                            onClick={() => {
                              setSelectedReport(report);
                              setComment("");
                              setReviewDialogOpen(true);
                            }}
                          >
                            {t("report.review")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("report.review_title")}</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="rounded-lg bg-zinc-50 p-3 dark:bg-zinc-900 text-sm">
                <p className="font-medium">#{selectedReport.question_number}</p>
                <p className="text-zinc-500 mt-1">
                  {reasonLabel(selectedReport.reason)}
                </p>
                {selectedReport.description && (
                  <p className="mt-2 text-zinc-600 dark:text-zinc-400">
                    {selectedReport.description}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("report.admin_comment_label")}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  placeholder={t("report.admin_comment_placeholder")}
                />
              </div>
              <DialogFooter className="gap-2">
                <DialogClose render={
                  (props) => (
                    <Button variant="outline" className="cursor-pointer" {...props}>
                      {t("common.cancel")}
                    </Button>
                  )
                }>
                </DialogClose>
                <Button
                  variant="outline"
                  className="cursor-pointer text-zinc-500"
                  disabled={reviewing}
                  onClick={() => handleReview("dismiss")}
                >
                  {reviewing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="mr-2 h-4 w-4" />
                  )}
                  {t("report.dismiss")}
                </Button>
                <Button
                  className="cursor-pointer"
                  disabled={reviewing}
                  onClick={() => handleReview("resolve")}
                >
                  {reviewing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                  )}
                  {t("report.resolve")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
